import { FC, useEffect, useMemo, useRef, useState } from "react";
import STableLayout, {
  DEFAULT_PAGE_SIZE,
  TableLayoutRef,
} from "@/styles/STableLayout";
import { RequestHomeWrap } from "./style";
import useDataList from "@/hooks/useDataList";
import { ColumnsType } from "antd/lib/table";
import { AnyObject } from "antd/lib/_util/type";
import { Button, Input, TableColumnsType, Tag } from "antd";
import { useTranslation } from "react-i18next";
import { CommonListParams } from "@/types/common";
import {
  QuotationRequestItem,
  QuotationRequestItemStatus,
} from "@/types/quotationRequestItem";
import SText from "@/styles/SText";
import dayjs from "dayjs";
import apis from "@/apis/supplier";
import { getStorage } from "@/utils/storage";
import useQuotationStatus from "./hooks/useQuotationStatus";
import QuotationForm from "@/components/QuotationForm";
import CountdownTimer from "./components/CountdownTimer";
import { multi } from "@/utils/calc";
import { useLocation } from "react-router";

const RequestHome: FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const tableRef = useRef<TableLayoutRef | null>(null);

  const { getQuotationStatus } = useQuotationStatus();
  const [keyword, setKeyword] = useState<string>("");
  const [quotationRequestItems, setQuotationRequestItems] = useState<
    Array<QuotationRequestItem>
  >([]);
  const [formKey, setFormKey] = useState<"detail" | "add" | "">(""); // detail 或者 'add' 或者 ''
  const [detailId, setDetailId] = useState<string>("");

  const pageType = useMemo(() => {
    if (location.search) {
      const params = new URLSearchParams(location.search);
      const pageType = params.get("pageType") || "";
      const pageTypeArr = [];
      if (pageType === "pending") {
        pageTypeArr.push(
          QuotationRequestItemStatus.READ,
          QuotationRequestItemStatus.UN_READ
        );
      } else if (pageType === "done") {
        pageTypeArr.push(QuotationRequestItemStatus.RESOLVE);
      }

      return pageTypeArr;
    }
  }, [location.search]);

  const [, total, listLoading, { fetchList: fetchQuotationRequestItems }] =
    useDataList<QuotationRequestItem, [string, CommonListParams]>({
      fetchApi:
        (pageType && pageType?.length > 0)
          ? apis.supplierQuotationRequest
          : apis.supplierTimeOutQuotationRequest,
    });

  useEffect(() => {
    if (pageType) {
      fetchList(0, keyword, pageType || []);
    }
  }, [pageType])

  const fetchList = (offset: number, keyword: string, ints: Array<number>) => {
    const take = tableRef.current?.pageSize ?? DEFAULT_PAGE_SIZE;
    fetchQuotationRequestItems(getStorage("SUPPLIER_ID"), {
      body: keyword,
      pagination: { offset, take },
      ints,
    }).then(({ body }) => {
      setQuotationRequestItems(body ?? []);
    });
  };

  const columns: TableColumnsType<QuotationRequestItem> = [
    {
      title: t("supplierQuotation.status"),
      dataIndex: "status",
      render: (_, quotationRequestItem) => {
        const info = getQuotationStatus(quotationRequestItem?.status);
        return <Tag color={info.color}>{info.title}</Tag>;
      },
    },
    {
      title: t("supplierQuotation.productCode"),
      dataIndex: "productCode",
      render: (_, quotationRequestItem) => (
        <SText
          style={{ width: 80 }}
          text={quotationRequestItem?.requirementItem?.product?.code || ""}
        />
      ),
    },
    {
      title: t("supplierQuotation.productName"),
      dataIndex: "productName",
      render: (_, quotationRequestItem) => (
        <SText
          style={{ width: 100 }}
          text={quotationRequestItem?.requirementItem?.product?.name || ""}
        />
      ),
    },
    {
      title: t("supplierQuotation.productModel"),
      dataIndex: "productName",
      render: (_, quotationRequestItem) => (
        <SText
          style={{ width: 100 }}
          text={quotationRequestItem?.requirementItem?.product?.model || ""}
        />
      ),
    },
    {
      title: t("supplierQuotation.productCount"),
      dataIndex: "productCount",
      render: (_, quotationRequestItem) => (
        <SText style={{ width: 80 }} text={quotationRequestItem?.count || ""} />
      ),
    },
    {
      title: <SText text={t("supplierQuotation.mrd")} />,
      width: 120,
      dataIndex: "mrd",
      render: (_, quotationRequestItem) => (
        <SText text={dayjs(quotationRequestItem?.mrd).format(
          "YYYY-MM-DD"
        )} />
      ),
    },
    {
      title: <SText text={t("supplierQuotation.deadline")} />,
      dataIndex: "deadline",
      render: (_, quotationRequestItem) => (
        <SText
          text={dayjs(quotationRequestItem?.deadline).format(
            "YYYY-MM-DD HH:00"
          )}
        />
      ),
    },
    {
      title: t("supplierQuotation.price"),
      dataIndex: "price",
      render: (_, quotationRequestItem) => quotationRequestItem.price?.price,
    },
    {
      title: t("supplierQuotation.curPrice"),
      dataIndex: "curPrice",
      render: (_, quotationRequestItem) => quotationRequestItem.supplierPrice,
    },
    {
      title: t("supplierQuotation.mpq"),
      dataIndex: "mpq",
      render: (_, quotationRequestItem) => quotationRequestItem.price?.mpq,
    },
    {
      title: t("supplierQuotation.curMpq"),
      dataIndex: "curMpq",
      render: (_, quotationRequestItem) => quotationRequestItem.mpq,
    },
    {
      title: t("supplierQuotation.days"),
      dataIndex: "days",
      render: (_, quotationRequestItem) => quotationRequestItem.price?.days,
    },
    {
      title: t("supplierQuotation.curDays"),
      dataIndex: "curDays",
      render: (_, quotationRequestItem) => quotationRequestItem.days,
    },
    {
      title: t("supplierQuotation.curTotalPrice"),
      dataIndex: "curTotalPrice",
      render: (_, quotationRequestItem) => {
        const { mpq, count } = quotationRequestItem;
        const countNumber = Number(count);
        const mpqNumber = mpq ? Number(mpq) : 0;
        let finalNum = 0;
        // 以最小包装计算
        if (mpq) {
          finalNum = Math.ceil(countNumber / mpqNumber);
        } else {
          finalNum = countNumber;
        }
        return multi(finalNum.toString(), quotationRequestItem.supplierPrice);
      },
    },
    {
      title: <SText text={t("supplierQuotation.submitTimer")} />,
      width: 100,
      fixed: "right",
      dataIndex: "deadline",
      hidden: !pageType?.includes(QuotationRequestItemStatus.READ) || !pageType?.includes(QuotationRequestItemStatus.UN_READ),
      render: (_, quotationRequestItem) => (
        <CountdownTimer
          onDue={() => { }}
          targetTime={dayjs(quotationRequestItem?.deadline)}
        />
      ),
    },
    {
      title: t("supplierQuotation.operation"),
      width: 100,
      fixed: "right",
      dataIndex: "opt",
      hidden: !pageType?.includes(QuotationRequestItemStatus.RESOLVE) && !pageType?.includes(QuotationRequestItemStatus.READ),
      render: (_, quotationRequestItem) => {
        if (
          [
            QuotationRequestItemStatus.UN_READ,
            QuotationRequestItemStatus.READ,
          ].includes(quotationRequestItem?.status)
        ) {
          return (
            <Button
              type="link"
              onClick={() => {
                setFormKey("add");
                setDetailId(quotationRequestItem.id);
              }}
            >
              {t('supplierQuotation.handlePrice')}
            </Button>
          );
        } else if (
          quotationRequestItem?.status === QuotationRequestItemStatus.RESOLVE
        ) {
          return (
            <Button
              type="link"
              onClick={() => {
                setFormKey("detail");
                setDetailId(quotationRequestItem.id);
              }}
            >
              {t('supplierQuotation.priceInfo')}
            </Button>
          );
        } else if (
          quotationRequestItem?.status === QuotationRequestItemStatus.REJECT
        ) {
          return (
            <Button type="link" danger>
              {t('supplierQuotation.rejected')}
            </Button>
          );
        }
      },
    },
  ];

  return (
    <RequestHomeWrap style={{ width: "100%", height: "100%" }}>
      <STableLayout
        loading={listLoading}
        dataSource={quotationRequestItems}
        rowKey="id"
        scrollX={1800}
        columns={columns as ColumnsType<AnyObject>}
        headerChildren={
          <Input.Search
            enterButton
            placeholder={t("requirement.searchPro")}
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            onSearch={(e) => {
              fetchList(0, e, pageType || []);
              tableRef?.current?.resetTablePage();
            }}
          />
        }
        tableLayoutRef={tableRef}
        total={total}
        handleChange={(offset) => {
          fetchList(offset, keyword, pageType || []);
        }}
      />

      <QuotationForm
        formKey={formKey}
        detailId={detailId}
        onCancel={() => {
          setFormKey("");
          setDetailId("");
        }}
        onSubmit={() => {
          setQuotationRequestItems(list => list.filter(item => item.id !== detailId));
        }}
        onReject={() => {
          setQuotationRequestItems(list => list.filter(item => item.id !== detailId));
        }}
      />
    </RequestHomeWrap>
  );
};

export default RequestHome;
